import { matchIsValidTel } from "mui-tel-input";
import { z } from "zod";
import passwordStrength from "@/core/utils/password-validation";

export const emailSchema = z.object({
	email: z.string().min(1, "email.required").pipe(z.email("email.invalid")),
});

const passwordComplexitySchema = z
	.string()
	.refine((pw) => passwordStrength(pw), {
		message: "password.rules",
	});

const passwordBaseSchema = z.object({
	password: passwordComplexitySchema,
	password_confirmation: z.string().min(1, "password.required"),
});

// Schema for form validation (includes passwordConfirmation)
export const passwordSchema = passwordBaseSchema.refine(
	({ password, password_confirmation }) => password === password_confirmation,
	{
		message: "password_confirmation.invalid",
		path: ["password_confirmation"],
	},
);

const storePasswordSchema = passwordBaseSchema.omit({
	password_confirmation: true,
});

const userProfileBaseSchema = z.object({
	given_name: z
		.string()
		.min(2, "given_name.required")
		.max(60, "given_name.invalid"),
	phone_number: z
		.string()
		.min(1, "phone_number.required")
		.max(15, "phone_number.invalid")
		.regex(/^\+(?:[0-9\-()/.]\s?){6,15}[0-9]$/, "phone_number.invalid"),
	family_name: z
		.string()
		.min(2, "family_name.required")
		.max(60, "family_name.invalid"),
	terms_accept: z.boolean().refine((val) => val === true, {
		message: "terms_accept.required",
		path: ["terms_accept"],
	}),
});

export const userProfileSchema = userProfileBaseSchema.refine(
	({ phone_number }) => matchIsValidTel(phone_number),
	{ message: "phone_number.invalid", path: ["phone_number"] },
);

export const confirmationSchema = z.object({
	code: z
		.string()
		.min(1, "confirmation.required")
		.max(8, "confirmation.invalid"),
});

export const registrationSchema = z.object({
	user_id: z.string().optional(),
	...emailSchema.shape,
	...storePasswordSchema.shape,
	...userProfileBaseSchema.shape,
});

export type EmailData = z.infer<typeof emailSchema>;
export type PasswordData = z.infer<typeof passwordSchema>;
export type UserProfileData = z.infer<typeof userProfileSchema>;
export type ConfirmationData = z.infer<typeof confirmationSchema>;
export type RegistrationData = z.infer<typeof registrationSchema>;
